import { useContext, useReducer } from 'react'

// TYPES
import type { IToast } from '@components/molecules/toast'

// UTILITIES
import * as FixedActions from './actions'
import { FixedContext, FixedReducer, FixedState, initState } from './reducer'


/**
 * Instantiates a new `Fixed` store and returns the state and dispatch in an object for use in a `FixedContext.Provider`.\
 * Only one Reducer is needed per Provider, and as such, this hook should be used as close to it's designated Provider as possible.
 * ### Use the `useFixedState` and `useFixedDispatch` hooks to access Reducer data.
 * 
 * @returns A pre-formatted object a FixedProvider will accept as a value containing the Reducer's State and Dispatch.
 */
export const useFixedReducer = () => {
    const [localState, localDispatch] = useReducer(FixedReducer, initState)
    return { localState, localDispatch }
}

/**
 * Access the nearest `Fixed` Provider and returns the full state.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns The full `FixedState` object.
 */
export const useFixedState = () => {
    const localState = useContext(FixedContext).localState
    if (!localState) throw new Error('useFixedState must be used within an FixedContext Provider')
    return localState
}

/**
 * Access the nearest `Fixed` Provider and returns it's Dispatch method.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns A Dispatch method that accepts `FixedActionTypes` for the purposes of updating the Fixed state.
 */
export const useFixedDispatch = () => {
    const localDispatch = useContext(FixedContext).localDispatch
    if (!localDispatch) throw new Error('useFixedDispatch must be used within an FixedContext Provider')
    return localDispatch
}


type FixedSettings = () => {
    /**
     * A quick way of appending data to the `toast` variable in the Fixed store.
     *
     * @param props Accepts either a `string` or `IToast` arguments. By default, if typeof string, the Toast displayed will be of type error.
     * @returns 
     */
    createToast: (props: string | IToast) => void

    /**
     * A copy of the state in the local FixedReducer.
     */
    state: FixedState
}

/**
 * Returns an object of pre-formatted utilities that control certain aspects of Fixed template.
 */
export const useFixedSettings: FixedSettings = () => {
    const state = useFixedState()
    const localDispatch = useFixedDispatch()

    function createToast(props: string | IToast) {
        localDispatch(FixedActions.setToast(typeof props === 'string' ? {
            type: 'error',
            text: props,
        } : props))
    }

    return { createToast, state }
}